import { useCallback, useEffect, useState } from "react";

import {
  getStorageBreakdown,
  getStorageEstimate,
  warningLevelFor,
  type StorageBreakdown,
  type StorageEstimate,
  type WarningLevel,
} from "@/db/quota";

/** Default auto-refresh interval (ms) used by the Privacy Inspector. */
export const QUOTA_REFRESH_MS = 30_000;

export interface UseQuotaOptions {
  /** Poll on an interval (e.g. while the Privacy Inspector is open). */
  autoRefresh?: boolean;
  intervalMs?: number;
}

export interface UseQuotaResult {
  estimate: StorageEstimate | null;
  breakdown: StorageBreakdown | null;
  warningLevel: WarningLevel;
  refresh: () => Promise<void>;
  isLoading: boolean;
}

/**
 * Live storage-quota state: the browser estimate, a per-category breakdown, and
 * a derived warning level. Refreshes once on mount and, when `autoRefresh` is
 * set, every `intervalMs` (default 30s) — used by the Privacy Inspector.
 */
export function useQuota(options: UseQuotaOptions = {}): UseQuotaResult {
  const { autoRefresh = false, intervalMs = QUOTA_REFRESH_MS } = options;
  const [estimate, setEstimate] = useState<StorageEstimate | null>(null);
  const [breakdown, setBreakdown] = useState<StorageBreakdown | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const refresh = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    try {
      const [nextEstimate, nextBreakdown] = await Promise.all([
        getStorageEstimate(),
        getStorageBreakdown(),
      ]);
      setEstimate(nextEstimate);
      setBreakdown(nextBreakdown);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    if (!autoRefresh) return;
    const handle = setInterval(() => void refresh(), intervalMs);
    return () => clearInterval(handle);
  }, [refresh, autoRefresh, intervalMs]);

  const warningLevel = warningLevelFor(estimate?.percent ?? 0);
  return { estimate, breakdown, warningLevel, refresh, isLoading };
}
